import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const SubscriptionsList = ({ subscriptions, plans, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [loading, setLoading] = useState(false);

  const filteredSubscriptions = subscriptions?.filter(sub => {
    const matchesSearch =
      sub?.user_profiles?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      sub?.user_profiles?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || sub?.status === filterStatus;
    const matchesPlan = filterPlan === 'all' || sub?.plan_id === filterPlan;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: 'bg-success/10 text-success',
      paused: 'bg-warning/10 text-warning',
      cancelled: 'bg-destructive/10 text-destructive',
      expired: 'bg-muted text-muted-foreground',
      pending: 'bg-primary/10 text-primary'
    };
    return classes?.[status] || 'bg-muted text-muted-foreground';
  };

  const handleUpdateStatus = async (subscriptionId, newStatus) => {
    setLoading(true);
    try {
      const { error } = await supabase?.from('member_subscriptions')?.update({ status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', subscriptionId);

      if (error) throw error;
      onRefresh?.();
    } catch (err) {
      console.error('Failed to update subscription status:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
          <Icon name="List" size={24} color="var(--color-primary)" />
          Member Subscriptions
        </h2>
        <Button onClick={onRefresh} variant="ghost" size="sm" disabled={loading}>
          <Icon name="RefreshCw" size={18} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="relative">
          <Icon
            name="Search"
            size={18}
            color="var(--color-muted-foreground)"
            className="absolute left-3 top-1/2 -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e?.target?.value)}
          className="px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
          <option value="pending">Pending</option>
        </select>
        <select
          value={filterPlan}
          onChange={(e) => setFilterPlan(e?.target?.value)}
          className="px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
        >
          <option value="all">All Plans</option>
          {plans?.map((plan) => (
            <option key={plan?.id} value={plan?.id}>
              {plan?.plan_name}
            </option>
          ))}
        </select>
      </div>

      {/* Subscriptions Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Member</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Plan</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Status</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Price</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Start Date</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Next Billing</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubscriptions?.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-8 text-muted-foreground">
                  No subscriptions found
                </td>
              </tr>
            ) : (
              filteredSubscriptions?.map((sub) => (
                <tr key={sub?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary">
                          {sub?.user_profiles?.full_name?.charAt(0)?.toUpperCase() || 'M'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{sub?.user_profiles?.full_name}</p>
                        <p className="text-xs text-muted-foreground">{sub?.user_profiles?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-medium text-foreground">
                      {sub?.membership_plans?.plan_name || 'N/A'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(sub?.status)}`}>
                      {sub?.status?.charAt(0)?.toUpperCase() + sub?.status?.slice(1)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-semibold text-foreground">${sub?.final_price?.toFixed(2)}</p>
                      {sub?.discount_percentage > 0 && (
                        <p className="text-xs text-success">-{sub?.discount_percentage}% off</p>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">{formatDate(sub?.start_date)}</td>
                  <td className="py-3 px-4 text-sm text-foreground">{formatDate(sub?.next_billing_date)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" title="View Details">
                        <Icon name="Eye" size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" title="Edit">
                        <Icon name="Edit" size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubscriptionsList;